import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CalendarDays, HandCoins, Image as ImageIcon, MapPin, MapPinned, Pencil, Phone, PiggyBank } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatCard, StatusPill } from "@/components/common";
import { TxTypeBadge } from "@/components/TxTable";
import { AuthImage } from "@/components/AuthImage";
import { MemberForm } from "@/pages/MembersPage";
import { MemberMap } from "@/components/MemberMap";
import { CameraCapture } from "@/components/CameraCapture";
import { fmtDate, fmtDateTime, FREQ_LABELS, LOAN_STATUS, loanTenor, ROW_TONE, rupiah, txTone } from "@/lib/format";

const SAVING_TYPES = ["setor", "tarik", "potongan"];

const SavingsTable = ({ rows }) => (
  rows.length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center" data-testid="member-savings-empty">Belum ada transaksi simpanan.</p> : (
    <div className="overflow-x-auto" data-testid="member-savings-table">
      <Table>
        <TableHeader><TableRow><TableHead>Tanggal</TableHead><TableHead>Jenis</TableHead><TableHead>Petugas</TableHead><TableHead className="text-right">Nominal</TableHead><TableHead className="text-right">Saldo</TableHead></TableRow></TableHeader>
        <TableBody>
          {rows.map((t) => (
            <TableRow key={t.id} className={txTone(t.type)}>
              <TableCell className="text-sm whitespace-nowrap">{fmtDateTime(t.created_at)}</TableCell>
              <TableCell><TxTypeBadge type={t.type} /></TableCell>
              <TableCell className="text-sm">{t.by_name}</TableCell>
              <TableCell className={`text-right font-mono ${t.type === "tarik" ? "text-rose-500" : ""}`}>{t.type === "tarik" ? "-" : "+"}{rupiah(t.amount)}</TableCell>
              <TableCell className="text-right font-mono">{t.balance_after !== undefined ? rupiah(t.balance_after) : "-"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
);

const schedule = (l, payments) => {
  const byNo = Object.fromEntries(payments.filter((p) => p.loan_id === l.id).map((p) => [p.installment_no, p]));
  return Array.from({ length: l.installment_count }, (_, i) => ({
    no: i + 1, due: Math.max(0, Math.min(l.installment_amount, l.total_payable - l.installment_amount * i)), pay: byNo[i + 1],
  }));
};

const ProofButton = ({ l }) => {
  const [open, setOpen] = useState(false);
  if (!l.photo_path) return null;
  return (
    <>
      <button type="button" className="inline-flex items-center gap-1 rounded-full border px-3 h-8 text-xs font-medium hover:bg-muted" onClick={() => setOpen(true)} data-testid={`loan-proof-btn-${l.id}`}>
        <ImageIcon className="h-3.5 w-3.5" />Lihat Foto
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="loan-proof-dialog">
          <DialogHeader><DialogTitle className="font-heading">Bukti Pencairan {l.loan_no || ""}</DialogTitle></DialogHeader>
          {open && <AuthImage path={`/loans/${l.id}/photo`} alt="Bukti pencairan" className="w-full min-h-[240px] rounded-2xl object-contain max-h-[70vh]" testId="loan-proof-image" />}
          <p className="text-xs text-muted-foreground">{l.disbursed_at ? `Dicairkan ${fmtDateTime(l.disbursed_at)} oleh ${l.disbursed_by_name || "-"}` : "Belum dicairkan"}{l.photo_lat != null ? ` · GPS ${l.photo_lat.toFixed(6)}, ${l.photo_lng.toFixed(6)}` : ""}</p>
        </DialogContent>
      </Dialog>
    </>
  );
};

const LoanSchedule = ({ l, payments }) => (
  <div className="rounded-2xl border p-4 space-y-3" data-testid={`member-loan-${l.id}`}>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div><p className="font-mono font-semibold">{rupiah(l.principal)}</p><p className="text-xs text-muted-foreground">{loanTenor(l)}x {FREQ_LABELS[l.frequency].toLowerCase()} · {l.resort_name ? `Resort ${l.resort_name}` : "Tanpa resort"} · {fmtDate(l.created_at)}</p></div>
      <div className="text-right"><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${LOAN_STATUS[l.status].cls}`}>{LOAN_STATUS[l.status].label}</span><p className="text-xs mt-1">Sisa <span className="font-mono font-semibold">{rupiah(["berjalan"].includes(l.status) ? l.total_payable - l.paid_amount : 0)}</span></p><ProofButton l={l} /></div>
    </div>
    {["berjalan", "lunas"].includes(l.status) && (
      <div className="max-h-80 overflow-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Ke</TableHead><TableHead className="text-right">Tagihan</TableHead><TableHead>Tanggal Bayar</TableHead><TableHead className="text-right">Dibayar</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>
            {schedule(l, payments).map((s) => (
              <TableRow key={s.no}>
                <TableCell className="font-mono">{s.no}</TableCell>
                <TableCell className="text-right font-mono">{rupiah(s.due)}</TableCell>
                <TableCell className="text-sm">{s.pay ? fmtDateTime(s.pay.created_at) : "-"}</TableCell>
                <TableCell className="text-right font-mono">{s.pay ? rupiah(s.pay.amount) : "-"}</TableCell>
                <TableCell><span className={`text-xs font-semibold ${s.pay ? "text-primary" : "text-muted-foreground"}`}>{s.pay ? "Dibayar" : "Belum"}</span></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    )}
  </div>
);

const InfoRow = ({ icon: Icon, label, value, testId }) => (
  <div className="flex items-start gap-3 py-2.5 border-b last:border-0">
    <Icon className="h-4 w-4 shrink-0 text-muted-foreground mt-0.5" />
    <span className="text-sm text-muted-foreground w-32 shrink-0">{label}</span>
    <span className="text-sm font-medium min-w-0 break-words" data-testid={testId}>{value || "-"}</span>
  </div>
);

const KtpPhoto = ({ m, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ver, setVer] = useState(0);
  const upload = async (blob) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "ktp.jpg");
      await api.post(`/members/${m.nik}/ktp`, fd);
      toast.success("Foto KTP disimpan"); setOpen(false); setVer((v) => v + 1); onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mb-3" data-testid="member-ktp-section">
      {m.ktp_path ? (
        <AuthImage path={`/members/${m.nik}/ktp?v=${ver}`} alt="Foto KTP" className="w-full max-w-md aspect-[1.586] rounded-2xl object-cover border" testId="member-ktp-image" />
      ) : (
        <div className="w-full max-w-md aspect-[1.586] rounded-2xl border-2 border-dashed grid place-items-center text-sm text-muted-foreground" data-testid="member-ktp-empty">Belum ada foto KTP</div>
      )}
      <button type="button" onClick={() => setOpen(true)} className="mt-2 inline-flex items-center gap-1 rounded-full border px-3 h-8 text-xs font-medium hover:bg-muted" data-testid="member-ktp-upload">
        <ImageIcon className="h-3.5 w-3.5" />{m.ktp_path ? "Ganti Foto KTP" : "Ambil Foto KTP"}
      </button>
      <CameraCapture open={open} onOpenChange={setOpen} onConfirm={upload} busy={busy} title="Foto KTP" description="Posisikan KTP mendatar di dalam bingkai." initialFacing="environment" frame="card" />
    </div>
  );
};

const Profile = ({ m, onSaved }) => (
  <div className="panel" data-testid="member-profile-card">
    <div className="flex items-center gap-4 mb-3">
      <span className="h-14 w-14 rounded-2xl bg-primary/12 text-primary grid place-items-center font-heading text-2xl font-bold shrink-0">{m.name[0]}</span>
      <div className="min-w-0 flex-1">
        <p className="font-heading text-xl font-bold truncate" data-testid="member-name">{m.name}</p>
        <p className="font-mono text-xs text-muted-foreground" data-testid="member-nik">NIK {m.nik}</p>
      </div>
      <StatusPill active={m.active} testId="member-status" />
    </div>
    <KtpPhoto m={m} onSaved={onSaved} />
    <InfoRow icon={MapPin} label="Alamat" value={m.address} testId="member-address" />
    <InfoRow icon={Phone} label="No. WhatsApp" value={m.phone} testId="member-phone" />
    <InfoRow icon={CalendarDays} label="Tgl Bergabung" value={fmtDate(m.join_date)} testId="member-join-date" />
    <InfoRow icon={MapPinned} label="Resort" value={m.resort_name ? `${m.resort_name}${m.petugas_name ? ` · ${m.petugas_name}` : ""}` : "Tanpa resort"} testId="member-resort" />
  </div>
);

const LoanHistory = ({ loans }) => (
  loans.length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center" data-testid="member-loans-empty">Belum ada pinjaman.</p> : (
    <div className="overflow-x-auto" data-testid="member-loans-table">
      <Table>
        <TableHeader><TableRow><TableHead>Tanggal</TableHead><TableHead>No. Pinjaman</TableHead><TableHead>Tenor</TableHead><TableHead className="text-right">Pokok</TableHead><TableHead className="text-right">Total Tagihan</TableHead><TableHead className="text-right">Sisa</TableHead><TableHead>Status</TableHead><TableHead>Foto</TableHead></TableRow></TableHeader>
        <TableBody>
          {loans.map((l) => (
            <TableRow key={l.id} className={ROW_TONE.pinjaman} data-testid={`member-loan-${l.id}`}>
              <TableCell className="text-sm whitespace-nowrap">{fmtDate(l.loan_date || l.created_at)}</TableCell>
              <TableCell className="font-mono text-xs whitespace-nowrap">{l.loan_no || "-"}</TableCell>
              <TableCell className="text-sm whitespace-nowrap">{loanTenor(l)}x {FREQ_LABELS[l.frequency].toLowerCase()}</TableCell>
              <TableCell className="text-right font-mono whitespace-nowrap">{rupiah(l.principal)}</TableCell>
              <TableCell className="text-right font-mono whitespace-nowrap">{rupiah(l.total_payable)}</TableCell>
              <TableCell className="text-right font-mono whitespace-nowrap font-semibold">{rupiah(l.status === "berjalan" ? l.total_payable - l.paid_amount : 0)}</TableCell>
              <TableCell><span className={`rounded-full px-2.5 py-1 text-xs font-semibold whitespace-nowrap ${LOAN_STATUS[l.status].cls}`}>{LOAN_STATUS[l.status].label}</span></TableCell>
              <TableCell><ProofButton l={l} /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
);

const InstallmentHistory = ({ rows, loans }) => {
  const no = Object.fromEntries(loans.map((l) => [l.id, l.loan_no]));
  return rows.length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center" data-testid="member-installments-empty">Belum ada angsuran.</p> : (
    <div className="overflow-x-auto" data-testid="member-installments-table">
      <Table>
        <TableHeader><TableRow><TableHead>Tanggal</TableHead><TableHead>No. Pinjaman</TableHead><TableHead>Ke</TableHead><TableHead>Petugas</TableHead><TableHead className="text-right">Nominal</TableHead><TableHead className="text-right">Sisa</TableHead></TableRow></TableHeader>
        <TableBody>
          {rows.map((t) => (
            <TableRow key={t.id} className={ROW_TONE.angsuran}>
              <TableCell className="text-sm whitespace-nowrap">{fmtDateTime(t.created_at)}</TableCell>
              <TableCell className="font-mono text-xs whitespace-nowrap">{no[t.loan_id] || "-"}</TableCell>
              <TableCell className="font-mono">{t.installment_no ?? "-"}</TableCell>
              <TableCell className="text-sm">{t.by_name}</TableCell>
              <TableCell className="text-right font-mono whitespace-nowrap">{rupiah(t.amount)}</TableCell>
              <TableCell className="text-right font-mono whitespace-nowrap">{t.remaining_after !== undefined ? rupiah(t.remaining_after) : "-"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default function MemberDetailPage() {
  const { nik } = useParams();
  const { user } = useAuth();
  const [m, setM] = useState(null);
  const [tx, setTx] = useState([]);
  const [edit, setEdit] = useState(false);
  const canManage = ["admin_cabang", "kepala_bagian"].includes(user.role);
  const canEdit = canManage || user.role === "kasir";

  const load = useCallback(() => {
    api.get(`/members/${nik}`).then((r) => setM(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/transactions", { params: { nik, limit: 2000 } }).then((r) => setTx(r.data)).catch(() => setTx([]));
  }, [nik]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (active) => {
    try { await api.patch(`/members/${nik}/status`, { active }); load(); toast.success(active ? "Anggota diaktifkan" : "Anggota dinonaktifkan"); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!m) return <p className="text-muted-foreground">Memuat...</p>;
  const payments = tx.filter((t) => t.type === "angsuran");
  const totalPinjaman = m.loans.filter((l) => ["berjalan", "lunas"].includes(l.status)).reduce((s, l) => s + l.principal, 0);

  return (
    <div className="max-w-5xl">
      <div className="flex items-center justify-between gap-3 mb-4">
        <Link to="/anggota" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground" data-testid="member-back-link"><ArrowLeft className="h-4 w-4 mr-1" />Kembali</Link>
        <div className="flex items-center gap-3">
          {canManage && <label className="flex items-center gap-2 text-sm"><Switch checked={m.active} onCheckedChange={toggle} data-testid="member-status-toggle" />{m.active ? "Aktif" : "Non-aktif"}</label>}
          {canEdit && <Button variant="outline" className="rounded-full h-10" onClick={() => setEdit(true)} data-testid="member-edit-button"><Pencil className="h-4 w-4 mr-2" />Ubah</Button>}
        </div>
      </div>
      <div className="space-y-4 mb-6">
        <Profile m={m} onSaved={load} />
        <div className="grid grid-cols-2 gap-4">
          <StatCard label="Total Simpanan" value={rupiah(m.savings_balance)} icon={PiggyBank} testId="member-balance" />
          <StatCard label="Total Pinjaman" value={rupiah(totalPinjaman)} hint={`Sisa ${rupiah(m.outstanding)}`} icon={HandCoins} testId="member-running-loan" />
        </div>
      </div>
      {m.lat != null && (
        <div className="panel mb-6" data-testid="member-location">
          <p className="font-heading font-semibold mb-1">Lokasi Alamat</p>
          <p className="text-xs font-mono text-muted-foreground mb-3" data-testid="member-detail-coordinates">{m.lat.toFixed(6)}, {m.lng.toFixed(6)} · <a className="text-primary underline" href={`https://www.google.com/maps?q=${m.lat},${m.lng}`} target="_blank" rel="noreferrer">Buka di Google Maps</a></p>
          <MemberMap lat={m.lat} lng={m.lng} height={260} testId="member-detail-map" />
        </div>
      )}
      <Tabs defaultValue="pinjaman">
        <TabsList className="h-11 mb-4 w-full sm:w-auto grid grid-cols-3 sm:inline-flex"><TabsTrigger value="pinjaman" className="h-9" data-testid="member-tab-pinjaman">Pinjaman</TabsTrigger><TabsTrigger value="angsuran" className="h-9" data-testid="member-tab-angsuran">Angsuran</TabsTrigger><TabsTrigger value="simpanan" className="h-9" data-testid="member-tab-simpanan">Simpanan</TabsTrigger></TabsList>
        <TabsContent value="pinjaman"><div className="panel"><p className="font-heading font-semibold mb-3">Riwayat Pinjaman</p><LoanHistory loans={m.loans} /></div></TabsContent>
        <TabsContent value="angsuran"><div className="panel"><p className="font-heading font-semibold mb-3">Riwayat Angsuran</p><InstallmentHistory rows={payments} loans={m.loans} /></div></TabsContent>
        <TabsContent value="simpanan"><div className="panel"><p className="font-heading font-semibold mb-3">Riwayat Simpanan</p><SavingsTable rows={tx.filter((t) => SAVING_TYPES.includes(t.type))} /></div></TabsContent>
      </Tabs>
      <MemberForm open={edit} onOpenChange={setEdit} initial={m} onSaved={load} />
    </div>
  );
}
